import React, { useState, useRef, useLayoutEffect, useEffect } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, Input, Modal, ModalBody } from "reactstrap";
import { FormattedMessage, useIntl } from "react-intl";
import {
  PostWorkCardComment,
  SignWorkCardRegistration,
} from "../../actions/teacherMutations";
import { toast } from "react-toastify";
import useAuthenticatedImage from "../../hooks/useAuthenticatedImage";
import { formatDate, toDate } from "../../Utilities/dateUtils";
import { FaRegThumbsUp } from "react-icons/fa";
import { FaRegComment } from "react-icons/fa";
import { IoChevronBack, IoChevronForward } from "react-icons/io5";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPaperPlane,
  faCheck,
  faClock,
  faXmark,
} from "@fortawesome/free-solid-svg-icons";

const GALLERY_TILE_LIMIT = 4;

function AuthenticatedGalleryImage({ imageId, className, loading }) {
  const blobUrl = useAuthenticatedImage(`/Apl/WorkRegistrationImage/${imageId}`);
  if (!blobUrl) return null;
  return <img src={blobUrl} alt="" loading={loading} className={className} />;
}

function AuthenticatedLightboxImage({ imageId, className }) {
  const blobUrl = useAuthenticatedImage(`/Apl/WorkRegistrationImage/${imageId}`);
  if (!blobUrl) return null;
  return <img src={blobUrl} alt="" className={className} />;
}

const APLStudentEventCard = React.memo(function APLStudentEventCard({
  item,
  date,
  level,
  periodId,
  objectId,
}) {
  const intl = useIntl();
  const avatarUrl = useAuthenticatedImage(
    item.avatar ? `/User/ProfilePicture/${item.studentId}` : null,
  );
  const [showComments, setShowComments] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const [showFullText, setShowFullText] = useState(false);
  const [isTextTruncated, setIsTextTruncated] = useState(false);
  const textRef = useRef(null);
  const toggle = () => {
    setShowComments(!showComments);
    setValue("");
  };
  const [value, setValue] = useState("");

  const queryClient = useQueryClient();

  const handleChange = (event) => {
    setValue(event.target.value);
  };

  const postComment = () => {
    if (value !== "") {
      postWorkCardComment.mutate(
        {
          created: formatDate(new Date(), "yyyy-MM-dd HH:mm"),
          text: value,
          workRegistrationId: item.id,
        },
        {
          onSuccess: (data) => {
            item.comments.push(data.comments[0]);
            setValue("");
          },
          throwOnError: true,
          onError: (data) => {
            toast.warn(data.response.data);
          },
        },
      );
    }
  };

  const postWorkCardComment = useMutation({ mutationFn: PostWorkCardComment });
  const signWorkCardRegistration = useMutation({ mutationFn: SignWorkCardRegistration });

  const handleSignOrUnSignRegistration = () => {
    signWorkCardRegistration.mutate(
      {
        workRegistrationId: item.id,
        signed: !item.signedByTeacher,
      },
      {
        onSuccess: () => {
          queryClient.refetchQueries({
            queryKey: ["FetchEvents", [level, periodId, objectId]],
            exact: true,
          });
        },
        throwOnError: true,
        onError: (data) => {
          toast.warn(data.response.data);
        },
      },
    );
  };

  useLayoutEffect(() => {
    const element = textRef.current;
    if (element && !showFullText) {
      setIsTextTruncated(element.scrollHeight > element.clientHeight);
    }
  }, [item.bodyText, showFullText]);

  const initials = item.studentName
    .split(" ")
    .map((word) => word[0])
    .join("")
    .toUpperCase();

  const visibleImages = (item.images || []).slice(0, GALLERY_TILE_LIMIT);

  useEffect(() => {
    if (lightboxIndex === null || item.images?.length < 1) {
      return undefined;
    }

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setLightboxIndex(null);
      }

      if (event.key === "ArrowLeft" && item.images?.length > 1) {
        setLightboxIndex((prev) =>
          typeof prev === "number"
            ? (prev - 1 + item.images?.length) % item.images?.length
            : 0,
        );
      }

      if (event.key === "ArrowRight" && item.images?.length > 1) {
        setLightboxIndex((prev) =>
          typeof prev === "number" ? (prev + 1) % item.images?.length : 0,
        );
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [lightboxIndex, item.images?.length]);

  return (
    <>
      <Card key={item.id} className="apl-moment-card">
        <div className="d-flex align-items-center gap-2 mb-3">
          <div className="apl-moment-card--story" title={item.studentName}>
            {avatarUrl ? (
              <img
                src={avatarUrl}
                alt=""
                className="apl-moment-card--story--avatar"
              />
            ) : (
              <span className="apl-moment-card--story--initials">{initials}</span>
            )}
          </div>
          <div className="flex-grow-1" style={{ minWidth: 0 }}>
            <div className="apl-moment-card--student-name">
              {item.studentName}
            </div>
            <div className="apl-moment-card--date">
              {date.toLocaleDateString("sv-SE")}
            </div>
          </div>
          <div className="d-flex gap-1 flex-shrink-0">
            {item.signedByTeacher && (
              <span className="apl-moment-card--status-pill apl-moment-card--status-pill--teacher">
                <FontAwesomeIcon icon={faCheck} className="me-1" />
                <FormattedMessage id="Teacher" />
              </span>
            )}
            {item.signedBySupervisor && (
              <span className="apl-moment-card--status-pill apl-moment-card--status-pill--supervisor">
                <FontAwesomeIcon icon={faCheck} className="me-1" />
                <FormattedMessage id="Supervisor" />
              </span>
            )}
          </div>
        </div>

        <div className="d-flex align-items-center gap-1 flex-wrap mb-2">
          <span className="apl-moment-card--tag">
            {item.workStepComponent.workStepName}
          </span>
          <IoChevronForward size="0.7rem" color="#9ca3af" />
          <span className="apl-moment-card--tag">
            {item.workStepComponent.name}
          </span>
          <span className="mx-1 apl-moment-card--meta-divider">|</span>
          <span className="apl-moment-card--tag apl-moment-card--tag--hours">
            <FontAwesomeIcon icon={faClock} className="me-1" />
            {item.workStepComponent.workingHours}h
          </span>
        </div>

        <div className="mb-2">
          <div
            ref={textRef}
            className="apl-moment-card--body"
            style={
              showFullText
                ? {}
                : {
                    display: "-webkit-box",
                    WebkitLineClamp: 5,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }
            }
          >
            {item.bodyText}
          </div>
          {isTextTruncated && (
            <button
              type="button"
              className="apl-moment-card--readmore-btn"
              onClick={() => setShowFullText(!showFullText)}
            >
              <FormattedMessage id={showFullText ? "ShowLess" : "ReadMore"} />
            </button>
          )}
        </div>

        {visibleImages.length > 0 && (
          <div
            className={`apl-moment-card--gallery apl-moment-card--gallery--${visibleImages.length}`}
          >
            {visibleImages.map((image, i) => (
              <button
                type="button"
                key={`${item.id}-thumb-${i}`}
                className="apl-moment-card--image-item"
                onClick={() => setLightboxIndex(i)}
                aria-label={`${intl.formatMessage({ id: "ShowImage" })} ${i + 1}`}
              >
                <AuthenticatedGalleryImage
                  imageId={image}
                  loading="lazy"
                  className="apl-moment-card--image-thumb"
                />
                {i === visibleImages.length - 1 &&
                  item.images?.length > GALLERY_TILE_LIMIT && (
                    <span className="apl-moment-card--image-overflow">
                      +{item.images?.length - GALLERY_TILE_LIMIT}
                    </span>
                  )}
              </button>
            ))}
          </div>
        )}

        <div className="apl-moment-card--action-bar">
          <button
            type="button"
            className="apl-moment-card--action-btn"
            onClick={handleSignOrUnSignRegistration}
            style={item.signedByTeacher ? { color: "#22c55e" } : {}}
          >
            <FaRegThumbsUp size="1.1rem" />
          </button>
          <button
            type="button"
            className="apl-moment-card--action-btn"
            onClick={toggle}
          >
            <FaRegComment size="1.1rem" />
            <span className="apl-moment-card--action-text">
              <FormattedMessage id="Comment" />
            </span>
            {item.comments.length > 0 && (
              <span className="apl-moment-card--messageNumbers">
                {item.comments.length}
              </span>
            )}
          </button>
        </div>

        {showComments && (
          <div className="mt-3">
            {item.comments.map((comment) => {
              const commentDate = toDate(comment.commentDate);
              return (
                <div
                  className="apl-moment-card--comment-bubble"
                  key={"comment" + comment.id}
                >
                  <div>
                    <span className="apl-moment-card--comment-name">
                      {comment.commenterName}
                    </span>
                    <span className="apl-moment-card--comment-date">
                      {commentDate.toLocaleDateString("sv-SE")}
                    </span>
                  </div>
                  <div className="apl-moment-card--comment-text">
                    {comment.text}
                  </div>
                </div>
              );
            })}
            <div className="apl-moment-card--comment-input-wrapper mt-2 mb-1">
              <Input
                type="text"
                bsSize="sm"
                className="apl-moment-card--comment-input"
                placeholder={intl.formatMessage({ id: "WriteComment" })}
                value={value}
                onChange={handleChange}
                onKeyDown={(ev) => {
                  if (ev.key === "Enter") {
                    postComment();
                    ev.preventDefault();
                  }
                }}
              />
              <button
                type="button"
                className="apl-moment-card--comment-send-btn"
                onClick={postComment}
              >
                <FontAwesomeIcon icon={faPaperPlane} />
              </button>
            </div>
          </div>
        )}
      </Card>

      {lightboxIndex !== null && (
        <Modal
          isOpen
          toggle={() => setLightboxIndex(null)}
          size="lg"
          centered
          contentClassName="apl-moment-card--lightbox-content"
        >
          <ModalBody className="apl-moment-card--lightbox-body text-center p-0">
            <button
              type="button"
              className="apl-moment-card--lightbox-close"
              onClick={() => setLightboxIndex(null)}
              aria-label={intl.formatMessage({ id: "Close" })}
            >
              <FontAwesomeIcon icon={faXmark} />
            </button>

            {item.images?.length > 1 && (
              <button
                type="button"
                className="apl-moment-card--lightbox-nav apl-moment-card--lightbox-nav-prev"
                onClick={(e) => {
                  e.stopPropagation();
                  setLightboxIndex((prev) =>
                    typeof prev === "number"
                      ? (prev - 1 + item.images?.length) % item.images?.length
                      : 0,
                  );
                }}
                aria-label={intl.formatMessage({ id: "Previous" })}
              >
                <IoChevronBack color="#ffffff" size="1.4rem" />
              </button>
            )}

            <AuthenticatedLightboxImage
              imageId={item.images?.[lightboxIndex]}
              className="apl-moment-card--lightbox-image"
            />

            {item.images?.length > 1 && (
              <button
                type="button"
                className="apl-moment-card--lightbox-nav apl-moment-card--lightbox-nav-next"
                onClick={(e) => {
                  e.stopPropagation();
                  setLightboxIndex((prev) =>
                    typeof prev === "number"
                      ? (prev + 1) % item.images?.length
                      : 0,
                  );
                }}
                aria-label={intl.formatMessage({ id: "Next" })}
              >
                <IoChevronForward color="#ffffff" size="1.4rem" />
              </button>
            )}

            {item.images?.length > 1 && (
              <div className="apl-moment-card--lightbox-counter">
                {lightboxIndex + 1} / {item.images?.length}
              </div>
            )}
          </ModalBody>
        </Modal>
      )}
    </>
  );
});

export default APLStudentEventCard;
